import * as Yup from 'yup';
import { API_BASE_URL, APP_POOL_CONFIG, DEFAULT_REDIRECT_URL, SHOP_URL } from '@synergy/config';
import { Box, Link, Stack, styled } from '@mui/material';
import { ErrorIcon } from '@synergy/theme-next';
import { FeatureFlag, UserRole } from '@synergy/models';
import { Form, Formik } from 'formik';
import { FormattedMessage } from 'react-intl';
import { PoolButton, PoolFormikPasswordField, PoolFormikTextfield } from '@synergy/components-next';
import { ReactElement, useState } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { hasFeature, hasOneOf } from '@synergy/utils';

import { ErrorText } from '../common/components/error-text/error-text';
import { LoginApi } from '../common/api/login-api';
import { LoginLogo } from '../common/components/login-logo/login-logo';
import { OfficeLogoIcon } from '../common/style/icons/office-logo-icon';
import { PhoneLink } from '../common/components/phone-link/phone-link';
import Footer from '../common/components/footer/footer';

const redirectSupplier = process.env['NX_PUBLIC_APP_SUPPLIER_REDIRECT_URL'] ?? SHOP_URL;
const supplierRoles = [UserRole.SupplierAdmin, UserRole.Extranet];
const adminRoles = [
    UserRole.Accounting,
    UserRole.Admin,
    UserRole.Configuration,
    UserRole.Sales,
    UserRole.SuluAdmin,
    UserRole.SuperAdmin,
];

interface LoginData {
    email: string;
    password: string;
}

const StyledForm = styled(Form)(() => ({
    display: 'flex',
    flexDirection: 'column',
    maxWidth: 300,
}));

const initialValues: LoginData = {
    email: '',
    password: '',
};

export const Login = (): ReactElement => {
    const [searchParams] = useSearchParams();
    const redirectURL = searchParams.get('redirect_url');
    const status = searchParams.get('status');
    const [hasError, setError] = useState(!!status);
    const [statusCode, setStatusCode] = useState(status ?? '');

    const loginApi = new LoginApi(API_BASE_URL);

    const schema = Yup.object().shape({
        email: Yup.string().required('app.error.required'),
        password: Yup.string().required('app.error.required'),
    });

    const handleSubmit = async (values: LoginData) => {
        setError(false);
        setStatusCode('');

        const response = await loginApi.login(values.email, values.password);

        if (!response.success) {
            setStatusCode(`${response.errorCode || ''}`);
            setError(true);

            return;
        }

        if (response.success && response.result.email) {
            window.location.href = getRedirectUrl(response.result.roles);
        } else {
            setError(true);
        }
    };

    const getRedirectUrl = (roles: UserRole[]): string => {
        if (redirectURL) {
            return redirectURL;
        }

        if (hasOneOf(adminRoles, roles)) {
            return DEFAULT_REDIRECT_URL;
        } else if (hasOneOf(supplierRoles, roles)) {
            return redirectSupplier;
        }

        return SHOP_URL;
    };

    return (
        <>
            <Stack justifyContent="center" alignItems="center" flexGrow={1}>
                <LoginLogo redirectURL={redirectURL} />
                <Formik initialValues={initialValues} onSubmit={handleSubmit} validationSchema={schema}>
                    <StyledForm>
                        <PoolFormikTextfield
                            name="email"
                            sx={{ mb: 2 }}
                            label={<FormattedMessage id="app.login.email" />}
                            type="text"
                            fullWidth={true}
                            required={true}
                            autoFocus={true}
                            autoComplete="email"
                        />
                        <PoolFormikPasswordField
                            name="password"
                            label={<FormattedMessage id="app.login.password" />}
                            fullWidth={true}
                            required={true}
                            sx={{ mb: 2 }}
                            autoComplete="current-password"
                        />
                        {hasError && (
                            <ErrorText statusCode={statusCode}>
                                <ErrorIcon />
                            </ErrorText>
                        )}
                        <Link
                            sx={{
                                ':hover': { color: theme => theme.palette.common.black },
                            }}
                            component={RouterLink}
                            to={{
                                pathname: '/forgot-password',
                                search: window.location.search,
                            }}
                            replace={false}
                            underline="none"
                            typography="body2"
                        >
                            <FormattedMessage id="app.forgotPassword.title" />
                        </Link>
                        <PoolButton variant="contained" type="submit" fullWidth={true} sx={{ mt: 3 }}>
                            <FormattedMessage id="app.login.submit" />
                        </PoolButton>
                        {hasFeature(FeatureFlag.MicrosoftLogin) && (
                            <PoolButton
                                href={`${API_BASE_URL}/connect/office365?redirect_url=${
                                    redirectURL ?? DEFAULT_REDIRECT_URL
                                }`}
                                variant="outlined"
                                fullWidth={true}
                                color="secondary"
                                startIcon={<OfficeLogoIcon />}
                                sx={{ mt: 2 }}
                            >
                                <FormattedMessage id="app.login.office" />
                            </PoolButton>
                        )}
                    </StyledForm>
                </Formik>
            </Stack>
            <Footer>
                <FormattedMessage id="app.footer.noCredentials" />
                <Box>
                    <FormattedMessage
                        id="app.footer.questions"
                        values={{ poolConfigNameShort: APP_POOL_CONFIG['name_short'] }}
                    />{' '}
                    <PhoneLink />
                </Box>
            </Footer>
        </>
    );
};
